import { Box, Card, CardContent, CircularProgress, Grid, Typography } from "@mui/material";
import { getValueColor } from "../utils/uiUtils";
import { EegDataRecord, EEGMetric, EEGRecording } from "../types";
import { calculateGlobalMeanZscore, calculateImprovement, groupByBrainRegion } from "../utils/calculationsUtils";
import { ComparisonMetricDisplay } from "./ComparisonMetricDisplay";
import { useEegDataForMetric, useAllEegData } from "../utils/useEeegData";

type RegionalStateProps = {
    metric: EEGMetric;
    showDataInTable : (region: string, records: Array<EegDataRecord>) => void;
    firstEEG: EEGRecording;
    secondEEG?: EEGRecording;
    allEegData: ReturnType<typeof useAllEegData>;
};

export const RegionalState = ({ metric, showDataInTable,  secondEEG, allEegData }: RegionalStateProps) => {

    const { firstEEGData, secondEEGData, firstEEGDataStatus, isFetchingSecondEEGData } = useEegDataForMetric(
        metric.id,
        allEegData
    );

    if (firstEEGDataStatus === "loading") return <CircularProgress />;
    if (firstEEGDataStatus === "error") return <Typography>Error loading metric</Typography>;

    const separateByBrainRegion = groupByBrainRegion(firstEEGData);
    const separateComparisonByBrainRegion = groupByBrainRegion(secondEEGData);
    const capitalize = (str: string) => str.charAt(0).toUpperCase() + str.slice(1);
 
    if (firstEEGData.length === 0)
        return (
            <Typography sx={{ textAlign: "center", color: "text.secondary" }}>
                De acordo com os filtros selecionados, não há dados para exibir
            </Typography>
        );

    return (
        <Grid container spacing={2}>
            {Object.entries(separateByBrainRegion).map(([region, records]) => {
                const mean = calculateGlobalMeanZscore(records);
                const comparisonRecords = separateComparisonByBrainRegion[region] || [];
                const comparisonMean = comparisonRecords.length > 0 ? calculateGlobalMeanZscore(comparisonRecords) : 0;
                const improvement = comparisonMean !== 0 ? calculateImprovement(mean, comparisonMean) : 0;
                const regionName = capitalize(region);
             
                return (
                    <Grid item xs={12} sm={6} md={2.4} key={region}>
                        <Card
                            sx={{
                                textAlign: "center",
                                backgroundColor: "#f8f9fa",
                                borderTop: `4px solid ${getValueColor(mean)}`,
                            }}
                        >
                            <CardContent>
                                <Box display="flex" justifyContent="center" alignItems="center">
                                    <Typography variant="subtitle2" color="textSecondary">
                                        {regionName}
                                    </Typography>
                                </Box>
                                <ComparisonMetricDisplay
                                    firstEEGMetricMean={mean}
                                    secondEEGMetricMean={comparisonMean}
                                    improvement={improvement}
                                    onFirstEEGClick={() => showDataInTable(regionName, records)}
                                    onSecondEEGClick={() => showDataInTable(regionName, comparisonRecords)}
                                    variant="h4"
                                    showComparison={!!secondEEG?.id}
                                    secondEEGIsFetching={isFetchingSecondEEGData}
                        
                                />
                            </CardContent>
                        </Card>
                    </Grid>
                );
            })}
        </Grid>
    );
};
